import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product, ProductVariant, CustomerInfo, PaymentInfo } from '@/lib/types'; // Ensure CustomerInfo includes cpf
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { fetchSystemSettings } from '@/lib/api';

export interface CartItem {
  product: Product;
  quantity: number;
  variant?: ProductVariant;
}

interface CartContextType {
  cartItems: CartItem[];
  addItem: (product: Product, quantity?: number, variant?: ProductVariant) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  totalPrice: number;
  customerInfo: CustomerInfo | null;
  setCustomerInfo: (info: CustomerInfo) => void;
  updatePaymentInfo: (paymentInfo: PaymentInfo) => void;
  sendOrderToWhatsApp: (storePhoneNumber: string) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['system-settings'], 
    queryFn: fetchSystemSettings,
    staleTime: Infinity, 
    enabled: !!customerInfo, 
  });

  useEffect(() => {
    const savedCart = localStorage.getItem('aquarium-cart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Failed to parse saved cart:', error);
      }
    }
    
    const savedCustomerInfo = localStorage.getItem('aquarium-customer-info');
    if (savedCustomerInfo) {
      try {
        setCustomerInfo(JSON.parse(savedCustomerInfo));
      } catch (error) {
        console.error('Failed to parse saved customer info:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('aquarium-cart', JSON.stringify(cartItems));
  }, [cartItems]);
  
  useEffect(() => {
    if (customerInfo) {
      localStorage.setItem('aquarium-customer-info', JSON.stringify(customerInfo));
    }
  }, [customerInfo]);

  const addItem = (product: Product, quantity = 1, variant?: ProductVariant) => {
    setCartItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(
        item => item.product.id === product.id && 
        (!variant || (item.variant?.id === variant.id))
      );

      if (existingItemIndex >= 0) {
        const newItems = [...prevItems];
        newItems[existingItemIndex].quantity += quantity;
        toast.success(`${product.name} adicionado ao carrinho`);
        return newItems;
      } else {
        toast.success(`${product.name} adicionado ao carrinho`);
        return [...prevItems, { product, quantity, variant }];
      }
    });
  };

  const removeItem = (itemId: string) => {
    setCartItems(prevItems => {
      const itemToRemove = prevItems.find(item => item.product.id === itemId);
      if (itemToRemove) {
        toast.success(`${itemToRemove.product.name} removido do carrinho`);
      }
      return prevItems.filter(item => item.product.id !== itemId);
    });
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    setCartItems(prevItems => 
      prevItems.map(item => 
        item.product.id === itemId 
          ? { ...item, quantity } 
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('aquarium-customer-info');
    setCustomerInfo(null);
  };

  const updatePaymentInfo = (paymentInfo: PaymentInfo) => {
    if (customerInfo) {
      setCustomerInfo({
        ...customerInfo,
        paymentInfo
      });
    }
  };

  const itemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  const totalPrice = cartItems.reduce((total, item) => {
    const price = item.variant ? item.variant.price : item.product.price;
    return total + (price * item.quantity);
  }, 0);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const sendOrderToWhatsApp = async (storePhoneNumber: string) => {
    if (!customerInfo || !customerInfo.paymentInfo || cartItems.length === 0) {
      toast.error('Informações do pedido incompletas para envio.');
      console.error('Missing customerInfo, paymentInfo, or cartItems');
      return;
    }

    const paymentMethod = customerInfo.paymentInfo.method || 'pix';
    const paymentReceiptBase64 = customerInfo.paymentInfo.receiptUrl;

    if (paymentMethod === 'pix' && !paymentReceiptBase64) {
      toast.error('Por favor, envie o comprovante de pagamento PIX antes de finalizar.');
      return;
    }
    if (paymentMethod === 'pix' && !customerInfo.pixKey) {
      toast.error('Por favor, informe sua chave PIX utilizada para pagamento.');
      return;
    }

    let orderResult: any = null;
    let functionError: any = null;
    try {
      const orderItemsPayload = cartItems.map(item => ({
        product_id: item.product.id,
        variant_id: item.variant?.id || null,
        product_name: item.product.name,
        variant_name: item.variant?.name || null,
        quantity: item.quantity,
        price: item.variant ? item.variant.price : item.product.price
      }));

      // Include CPF in the payload sent to the function
      const functionPayload = {
        customerInfo: { 
          name: customerInfo.name,
          phone: customerInfo.phone,
          cpf: customerInfo.cpf || null, // Add CPF here
          note: customerInfo.note || '',
          email: customerInfo.email || '',
          address: customerInfo.address || '',
          pixKey: customerInfo.pixKey || null,
          paymentInfo: {
            method: paymentMethod,
            receiptUrl: paymentReceiptBase64 || null, 
          }
        },
        cartItems: orderItemsPayload,
        totalPrice: totalPrice,
        needs_change: paymentMethod === 'cash' ? customerInfo.paymentInfo.needsChange : null,
        change_for: paymentMethod === 'cash' && customerInfo.paymentInfo.needsChange ? customerInfo.paymentInfo.changeFor : null
      };
      
      console.log("Invoking process-order function with payload:", JSON.stringify(functionPayload, null, 2));
      
      const { data, error } = await supabase.functions.invoke('process-order', {
         body: functionPayload
      });
      
      orderResult = data;
      functionError = error;

      if (functionError) {
        console.error('Error invoking Supabase function:', functionError);
        toast.error(`Erro ao salvar pedido no sistema: ${functionError.message || 'Erro desconhecido'}`);
        return; 
      } else {
         console.log("Order processed successfully by function:", orderResult);
         toast.success("Pedido salvo com sucesso no sistema!");
      }
     } catch (invokeError) {
        console.error("Critical error invoking process-order function:", invokeError);
        toast.error("Erro crítico ao tentar salvar o pedido no sistema.");
        return;
     }

    // --- Build WhatsApp Message ---
    const storeName = settings?.store_name || 'Sua Loja Online';
    let message = `*🛒 NOVO PEDIDO - ${storeName} 🛒*

`;
    message += `-------------------------------------
`;
    message += `*👤 Cliente:*
`;
    message += `*Nome:* ${customerInfo.name}
`;
    message += `*📞 Telefone:* ${customerInfo.phone}
`;
    if (customerInfo.cpf) message += `*📄 CPF:* ${customerInfo.cpf}
`; // Add CPF to message
    if (customerInfo.email) message += `*✉️ Email:* ${customerInfo.email}
`;
    if (customerInfo.address) message += `*📍 Endereço:* ${customerInfo.address}
`;
    message += `-------------------------------------
`;
    message += `*🛍️ Itens do Pedido:*
`;

    cartItems.forEach(item => {
      const price = item.variant ? item.variant.price : item.product.price;
      const variantText = item.variant ? ` (${item.variant.name})` : '';
      message += ` • ${item.quantity}x ${item.product.name}${variantText}
`;
      message += `   (${formatCurrency(price)} un. = *${formatCurrency(price * item.quantity)}*)
`;
    });

    message += `-------------------------------------
`;
    message += `*💰 Total do Pedido:* *${formatCurrency(totalPrice)}*
`;
    message += `-------------------------------------
`;
    message += `*💳 Pagamento:*
`;

    let paymentMethodDisplay: string;
    switch(paymentMethod) {
      case 'pix': paymentMethodDisplay = 'PIX'; break;
      case 'cash': paymentMethodDisplay = 'Dinheiro na Entrega'; break;
      case 'credit_card_machine': paymentMethodDisplay = 'Cartão Crédito (Maquininha)'; break;
      case 'debit_card_machine': paymentMethodDisplay = 'Cartão Débito (Maquininha)'; break;
      default: paymentMethodDisplay = 'Não especificado';
    }
    message += `*Método:* ${paymentMethodDisplay}
`;

    if (paymentMethod === 'pix') {
        message += `*Chave PIX Cliente:* ${customerInfo.pixKey || 'N/A'}
`;
        let receiptLinkText = 'NÃO ENVIADO';
        if (orderResult?.receipt_public_url) {
            receiptLinkText = `SIM - Ver: ${orderResult.receipt_public_url}`;
        } else if (paymentReceiptBase64) {
            receiptLinkText = 'ENVIADO (Erro no processamento/upload)';
        }
        message += `*Comprovante:* ${receiptLinkText}
`;
    }

    if (paymentMethod === 'cash' && customerInfo.paymentInfo?.needsChange) {
      message += `*Troco para:* ${formatCurrency(Number(customerInfo.paymentInfo?.changeFor) || 0)}
`;
    }

    if (customerInfo.note) {
      message += `-------------------------------------
`;
      message += `*📝 Observações:*
`;
      message += `${customerInfo.note}
`;
    }
    message += `-------------------------------------
`;
    if (orderResult?.order_id) {
      message += `*🆔 ID do Pedido:* ${orderResult.order_id}
`;
      message += `-------------------------------------
`;
    }

    // --- Open WhatsApp Link ---
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://wa.me/${storePhoneNumber}?text=${encodedMessage}`;

    try {
        console.log(`Opening WhatsApp link for: ${storePhoneNumber}`);
        window.open(whatsappLink, '_blank');
        toast.success('Pedido enviado! Finalize a conversa no WhatsApp.');
        clearCart(); // Clear cart only on full success
    } catch (waError){
        console.error("Could not open WhatsApp link:", waError);
        toast.error("Não foi possível abrir o WhatsApp. Verifique o console.");
        console.log(`----- WHATSAPP MESSAGE FALLBACK -----
${message}
----- END WHATSAPP MESSAGE -----`);
        // Decide if cart should be cleared on WhatsApp link failure
    }
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      itemCount,
      totalPrice,
      customerInfo,
      setCustomerInfo,
      updatePaymentInfo,
      sendOrderToWhatsApp
    }}>
      {children}
    </CartContext.Provider>
  );
};
